// Job locality (MCIP-23 groundwork, shared-components extraction).
//
// Job identity ENCODES locality — `ca-staging-safety.*` and `us-staging-safety.*`
// are different jobs on purpose (different deploys, data, owners). The backend
// derives ids as `<region>-<environment>-<suite>` (backend/lib/conventions.js,
// default template `us-staging-{suite}`); this is the frontend's read side of
// that convention. Ids that don't match the pattern (fixture jobs like
// `sqa-api-tests-fleet`) fall back to the default us/staging identity — the
// same default the template encodes.

/** Canonical pill order from the design board. Regions/environments outside
 *  this list still parse (the pattern wins) — these are only the display order. */
export const REGION_ORDER = ['us', 'eu', 'ca'];
// Keep in step with lib/region.js DEFAULT_ENVIRONMENTS (platform catalog).
export const ENVIRONMENT_ORDER = ['staging', 'preview', 'production'];

const ID_PATTERN = /^([a-z]{2})-([a-z]+)-(.+)$/;

export const DEFAULT_LOCALITY = Object.freeze({ region: 'us', environment: 'staging' });

/** { region, environment, suite } for a job id; defaults mirror the backend template. */
export function jobLocality(jobId) {
  const m = ID_PATTERN.exec(String(jobId || ''));
  if (!m) return { ...DEFAULT_LOCALITY, suite: String(jobId || '') };
  return { region: m[1], environment: m[2], suite: m[3] };
}

/** Jobs matching a region+environment selection. */
export function jobsInLocality(jobs, { region, environment }) {
  return (jobs || []).filter((j) => {
    const loc = jobLocality(j.job_id);
    return loc.region === region && loc.environment === environment;
  });
}

/**
 * Pill options for the shared TopBar: the canonical order first, then anything
 * the data carries beyond it (never hide real data), each flagged with whether
 * any job exists there — absent localities render disabled, not invented.
 */
export function localityOptions(jobs) {
  const present = { regions: new Set(), environments: new Set() };
  for (const j of jobs || []) {
    const loc = jobLocality(j.job_id);
    present.regions.add(loc.region);
    present.environments.add(loc.environment);
  }
  const merge = (canonical, seen) => [
    ...canonical,
    ...[...seen].filter((v) => !canonical.includes(v)).sort(),
  ].map((value) => ({ value, available: seen.has(value) }));
  return {
    regions: merge(REGION_ORDER, present.regions),
    environments: merge(ENVIRONMENT_ORDER, present.environments),
  };
}
